import React, { forwardRef, useContext, useImperativeHandle, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout, selectUserRoles } from '../features/auth/authSlice';
import { selectUserParams } from '../store/slices/userParamsSlice';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { StyleClass } from 'primereact/styleclass';
import { classNames } from 'primereact/utils';
import { Ripple } from 'primereact/ripple';
import { Tooltip } from 'primereact/tooltip';
import { LayoutContext } from './context/layoutcontext';
import AppSidebar from './AppSidebar';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { Roles } from '../utils/roleEnums';
import { warrantyTeamRoles } from '../utils/permissionChecker';

const AppTopbar = forwardRef((props, ref) => {
    const { onMenuToggle, showRightSidebar, showConfigSidebar, isHorizontal } = useContext(LayoutContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userParams = useSelector(selectUserParams);
    const userRoles = useSelector(selectUserRoles);
    const { t } = useLanguage();

    const menubuttonRef = useRef(null);
    const buttonref = useRef(null);
    const btnRef1 = useRef(null);
    const imgRef = useRef(null);
    const privacyRef = useRef(null);
    const settingsRef = useRef(null);
    const logoutRef = useRef(null);

    const onMenuButtonClick = () => {
        onMenuToggle();
    };

    const onConfigButtonClick = () => {
        showConfigSidebar();
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
    };

    const handleSettings = () => {
        const firstName = userParams?.firstName || '';
        const lastName = userParams?.lastName || '';

        const queryParams = new URLSearchParams();
        if (firstName) queryParams.append('firstName', firstName);
        if (lastName) queryParams.append('lastName', lastName);

        const queryString = queryParams.toString();
        const path = queryString ? `/dealer-management/dealerUserDefinition?${queryString}` : '/dealer-management/dealerUserDefinition';

        navigate(path);
    };

    const handlePendingApprovals = () => {
        navigate('/warranty-management/pending-approval-requests');
    };

    // Check if user has permission to view pending approvals
    // Uses warrantyTeamRoles except WARRANTY_TEAM
    const allowedRoles = warrantyTeamRoles.filter(role => role !== Roles.WARRANTY_TEAM);
    const canViewPendingApprovals = Array.isArray(userRoles) && 
        allowedRoles.some(role => userRoles.includes(role));

    useImperativeHandle(ref, () => ({
        menubutton: menubuttonRef.current
    }));

    return (
        <>
            <div className="layout-topbar">
                <div className="topbar-start">
                    <button ref={buttonref} type="button" className="topbar-menubutton p-link p-trigger transition-duration-300" onClick={onMenuButtonClick}>
                        <i className="pi pi-bars"></i>
                    </button>
                </div>
                <div className="layout-topbar-menu-section">
                    <AppSidebar sidebarRef={props.sidebarRef} />
                </div>
                <div className="topbar-end">
                    <ul className="topbar-menu">
                        <li className={classNames({ 'block topbar-item ': isHorizontal(), 'block sm:hidden topbar-item': !isHorizontal() })}>
                            <StyleClass nodeRef={btnRef1} selector="@next" enterClassName="hidden" enterActiveClassName="px-scalein" leaveToClassName="hidden" leaveActiveClassName="px-fadeout" hideOnOutsideClick>
                                <Button ref={btnRef1} type="button" icon="pi pi-search" className="p-button-text p-button-secondary"></Button>
                            </StyleClass>
                            <ul className="hidden topbar-menu active-topbar-menu p-3 w-15rem  z-5" style={{ bottom: '-5.8rem' }}>
                                <span className="p-input-icon-left w-full">
                                    <i className="pi pi-search"></i>
                                    <InputText type="text" placeholder="Search" className="w-full" />
                                </span>
                            </ul>
                        </li>

                        {/* Onay Bekleyen Talepler Butonu */}
                        {canViewPendingApprovals && (
                            <li className="topbar-item">
                                <Tooltip target=".pending-approvals-btn" content={t('pendingApprovalRequests.title')} position="bottom" />
                                <button
                                    onClick={handlePendingApprovals}
                                    className="p-ripple cursor-pointer pending-approvals-btn"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: '#ffffff',
                                        color: '#0B3187',
                                        border: '2px solid #0B3187',
                                        borderRadius: '6px',
                                        textDecoration: 'none',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        marginRight: '0.5rem'
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.backgroundColor = '#0B3187';
                                        e.currentTarget.style.color = '#ffffff';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ffffff';
                                        e.currentTarget.style.color = '#0B3187';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                    }}
                                >
                                    <i className="pi pi-clock" style={{ fontSize: '1rem' }}></i>
                                    <Ripple />
                                </button>
                            </li>
                        )}

                        {/* Geri Bildirim Butonu */}
                        <li className="topbar-item">
                            <a
                                href="https://karsansoruyor.kiraca.com.tr/ords/f?p=187:4"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-ripple cursor-pointer"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    padding: '0.5rem 0.75rem',
                                    backgroundColor: '#ffffff',
                                    color: '#0B3187',
                                    border: '2px solid #0B3187',
                                    borderRadius: '6px',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    marginRight: '0.5rem'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#0B3187';
                                    e.currentTarget.style.color = '#ffffff';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.15)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                    e.currentTarget.style.color = '#0B3187';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                }}
                            >
                                <i className="pi pi-comment" style={{ fontSize: '1rem' }}></i>
                                <span>{t('auth.feedback')}</span>
                                <Ripple />
                            </a>
                        </li>

                        <li className="topbar-item" style={{ marginRight: '0.5em', position: 'relative', zIndex: 9999 }}>
                            <StyleClass nodeRef={imgRef} selector="@next" enterClassName="hidden" enterActiveClassName="px-scalein" leaveToClassName="hidden" leaveActiveClassName="px-fadeout" hideOnOutsideClick>
                                <a ref={imgRef} className="p-ripple cursor-pointer">
                                    <Avatar icon="pi pi-user" className="border-round-xl" size="normal" />
                                    <Ripple />
                                </a>
                            </StyleClass>
                            <ul className="topbar-menu active-topbar-menu p-4 w-20rem hidden" style={{ zIndex: 9999 }}>
                                {userParams?.dealerFullName && (
                                    <li className="mb-3 pb-3 border-bottom-1 border-300">
                                        <div className="font-bold text-900 text-lg line-height-3" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                            {userParams.dealerFullName}
                                        </div>
                                        {userParams.userEmail && (
                                            <div className="text-600 text-sm mt-2 flex align-items-center gap-2" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                <i className="pi pi-envelope" style={{ fontSize: '0.875rem', lineHeight: '1', verticalAlign: 'middle' }}></i>
                                                <span style={{ lineHeight: '1' }}>{userParams.userEmail}</span>
                                            </div>
                                        )}
                                    </li>
                                )}
                                <li role="menuitem" className="m-0 mb-3">
                                    <StyleClass nodeRef={privacyRef} selector="@grandparent" enterClassName="hidden" enterActiveClassName="px-scalein" leaveToClassName="hidden" leaveActiveClassName="px-fadeout" hideOnOutsideClick>
                                        <a ref={privacyRef} className="flex align-items-center hover:text-primary-500 transition-duration-200">
                                            <i className="pi pi-fw pi-lock mr-2"></i>
                                            <span>{t('common.privacy')}</span>
                                        </a>
                                    </StyleClass>
                                </li>
                                <li role="menuitem" className="m-0 mb-3">
                                    <StyleClass nodeRef={settingsRef} selector="@grandparent" enterClassName="hidden" enterActiveClassName="px-scalein" leaveToClassName="hidden" leaveActiveClassName="px-fadeout" hideOnOutsideClick>
                                        <a ref={settingsRef} className="flex align-items-center hover:text-primary-500 transition-duration-200 cursor-pointer" onClick={handleSettings}>
                                            <i className="pi pi-fw pi-cog mr-2"></i>
                                            <span>{t('common.settings')}</span>
                                        </a>
                                    </StyleClass>
                                </li>
                                <li role="menuitem" className="m-0">
                                    <StyleClass nodeRef={logoutRef} selector="@grandparent" enterClassName="hidden" enterActiveClassName="px-scalein" leaveToClassName="hidden" leaveActiveClassName="px-fadeout" hideOnOutsideClick>
                                        <a ref={logoutRef} className="flex align-items-center hover:text-primary-500 transition-duration-200" onClick={handleLogout}>
                                            <i className="pi pi-fw pi-sign-out mr-2"></i>
                                            <span>{t('common.logout')}</span>
                                        </a>
                                    </StyleClass>
                                </li>
                            </ul>
                        </li>

                        <li className="topbar-item">
                            <LanguageSelector />
                        </li>

                        <li>
                            <Button type="button" icon="pi pi-cog" text className=" p-button-secondary flex-shrink-0" onClick={onConfigButtonClick}></Button>
                        </li>
                        <li>
                            <Button type="button" icon="pi pi-arrow-left" text className=" p-button-secondary flex-shrink-0" onClick={showRightSidebar}></Button>
                        </li>
                    </ul>
                </div>
            </div>
        </>
    );
});

AppTopbar.displayName = 'AppTopbar';

export default AppTopbar;
